export const formatDisplayDate = (input) => {
  let date;
  if(!input) return null; 

  // Handle different input types
  if (input instanceof Date) {
    date = input;
  } else if (typeof input === 'string') {
    date = new Date(input);
  } else if (typeof input === 'number') {
    date = new Date(input);
  } else {
    throw new Error('Invalid input: expected Date object, string, or number');
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date: could not parse input');
  }
  
  // Format the date
  const options = {
    weekday: 'short',  // 'Fri'
    month: 'short',    // 'Sep'
    day: 'numeric',    // '12'
    year: 'numeric'    // '2025'
  };
  
  return date.toLocaleDateString('en-US', options);
};

/**
 * Format a ship date from various input formats to a specified output format
 * 
 * Ship dates are date-only (no time component). When given a UTC timestamp like
 * "2026-01-16T05:00:00.000Z", we extract the UTC date (2026-01-16) and display
 * that calendar date regardless of the user's timezone.
 * 
 * @param {Date|string|number} input - Date to format
 * @param {string} format - Output format (default: 'YYYY-MM-DD')
 *                         Tokens: YYYY, MM, MMM, DD, D
 * @returns {string|null} Formatted date string or null if input is invalid
 * 
 * @example
 * formatShipDate("2026-01-16T05:00:00.000Z", "MM/DD/YYYY") // "01/16/2026"
 * formatShipDate("01/16/2026", "YYYY-MM-DD") // "2026-01-16"
 * formatShipDate("1/5/2026", "MMM D, YYYY") // "Jan 5, 2026"
 */
export const formatShipDate = (input, format = 'YYYY-MM-DD') => {
  let date;
  if (!input) return null;

  if (input instanceof Date) {
    date = input;
  } else if (typeof input === 'string') {
    // Handle ISO 8601 / UTC strings (from database/API)
    if (input.includes('T') || input.includes('Z')) {
      // Parse the full ISO string to get a proper Date object
      const tempDate = new Date(input);
      
      if (isNaN(tempDate.getTime())) {
        throw new Error('Invalid ISO date string');
      }
      
      // Extract the UTC date components (the calendar date we want to display)
      // This is critical: we use getUTC* methods to get the date in UTC,
      // not the local timezone
      const year = tempDate.getUTCFullYear();
      const month = tempDate.getUTCMonth();
      const day = tempDate.getUTCDate();
      
      // Create a new Date object at local midnight with those date parts
      // This ensures the calendar date displays correctly regardless of timezone
      date = new Date(year, month, day);
      
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      // Handle YYYY-MM-DD format (without time)
      const [year, month, day] = input.split('-').map(Number);
      date = new Date(year, month - 1, day);
      
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(input)) {
      // Handle MM/DD/YYYY or M/D/YYYY format
      const [month, day, year] = input.split('/').map(Number);
      date = new Date(year, month - 1, day);
      
    } else {
      // Fallback: try native Date parsing
      // Note: This is browser-dependent and may give inconsistent results
      date = new Date(input);
    }
  } else if (typeof input === 'number') {
    date = new Date(input);
  } else {
    throw new Error('Invalid input: expected Date object, string, or number');
  }

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date: could not parse input');
  }

  // Format the date using local time (since we normalized to local midnight above)
  const year = date.getFullYear();
  const monthPadded = String(date.getMonth() + 1).padStart(2, '0');
  const monthNum = String(date.getMonth() + 1);
  const dayPadded = String(date.getDate()).padStart(2, '0');
  const dayNum = String(date.getDate());
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthShort = monthNames[date.getMonth()];

  // Replace tokens from longest to shortest to avoid conflicts
  return format
    .replace('YYYY', year)
    .replace('MMM', monthShort)
    .replace('MM', monthPadded)
    .replace('DD', dayPadded)
    .replace(/\bM\b/, monthNum)   // Single M (no padding)
    .replace(/\bD\b/, dayNum);    // Single D (no padding)
};

export const calcNextShipDate = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  // Add one day
  date.setDate(date.getDate() + 1);

  // Skip weekends (Saturday = 6, Sunday = 0)
  while(date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

export const calcNextDeliveryDate = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  // Add one day
  date.setDate(date.getDate() + 1);

  // Skip weekends (Saturday = 6, Sunday = 0)
  while(date.getDay() !== 5) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

export const calcDeliverByDate = (fromDate = new Date()) => {
  const date = new Date(fromDate);

  for(let days = 0; days < 3; days++) {
    date.setDate(date.getDate() - 1);

    // Skip weekends (Saturday = 6, Sunday = 0)
    while(date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() - 1);
    }
  }

  return date;
}

export const calcDefaultShipDate = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
  const currentDay = date.getDay();
  
  // Calculate days until next Monday
  const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay);
  
  // Add days to get to next Monday, then add 14 more days (2 weeks)
  date.setDate(date.getDate() + daysUntilMonday + 14);
  
  return date;
}

export const calcDefaultDeliveryDate = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
  const currentDay = date.getDay();

  // Calculate days until next Friday
  const daysUntilDay = currentDay === 0 ? 1 : (5 - currentDay);

  // Add days to get to next Friday, then add 14 more days (2 weeks)
  date.setDate(date.getDate() + daysUntilDay + 14);

  return date;
}